import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Box,
  CircularProgress, Stack, Card, CardContent, TextField, Button, Chip, List, ListItem, ListItemText,
  ListItemSecondaryAction, Divider, Tabs, Tab, Badge, Alert, FormControl, InputLabel,
  Select, MenuItem, Tooltip
} from '@mui/material';
import {
  Close as CloseIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Build as BuildIcon, Schedule as ScheduleIcon, CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon, CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { interventionService, interventionSettingsService } from '../../services/api';
import type { Vehicle, Intervention, InterventionFormData } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`intervention-tabpanel-${index}`}
      aria-labelledby={`intervention-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface InterventionManagementProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const InterventionManagement: React.FC<InterventionManagementProps> = ({
  vehicle,
  open,
  onClose,
  onSuccess
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [interventionTypes, setInterventionTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingIntervention, setEditingIntervention] = useState<Intervention | null>(null);
  const [formData, setFormData] = useState<InterventionFormData>({
    vehicle_id: vehicle.id,
    intervention_type: '',
    description: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, vehicle.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [interventionsRes, typesRes] = await Promise.all([
        interventionService.getByVehicle(vehicle.id),
        interventionSettingsService.getSettings()
      ]);
      
      setInterventions(interventionsRes.data.data || []);
      
      // Process intervention types (now only client-specific)
      const settings = typesRes.data.data;
      setInterventionTypes(settings.types || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Ensure description has a default value if empty
      const submitData = {
        ...formData,
        description: formData.description || `Intervention ${formData.intervention_type}`
      };

      if (editingIntervention) {
        await interventionService.update(editingIntervention.id, submitData);
        setSuccess('Intervention modifiée avec succès!');
      } else {
        await interventionService.create(submitData);
        setSuccess('Intervention créée avec succès!');
      }
      
      // Clear form and reset state
      setFormData({
        vehicle_id: vehicle.id,
        intervention_type: '',
        description: '',
        start_date: format(new Date(), 'yyyy-MM-dd'),
      });
      setShowForm(false);
      setEditingIntervention(null);
      
      // Reload data to show the new/updated record
      await loadData();
      onSuccess();
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (intervention: Intervention) => {
    setEditingIntervention(intervention);
    setFormData({
      vehicle_id: vehicle.id,
      intervention_type: intervention.intervention_type,
      description: intervention.description || '',
      start_date: intervention.start_date ? format(new Date(intervention.start_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      completion_date: intervention.completion_date ? format(new Date(intervention.completion_date), 'yyyy-MM-dd') : undefined,
      cost: intervention.cost,
      service_provider: intervention.service_provider || '',
      notes: intervention.notes || '',
    });
    setShowForm(true);
    setTabValue(2); // Switch to the form tab
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette intervention ?')) {
      setLoading(true);
      try {
        await interventionService.delete(id);
        loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erreur lors de la suppression');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingIntervention(null);
    setFormData({
      vehicle_id: vehicle.id,
      intervention_type: interventionTypes.length > 0 ? interventionTypes[0].name : '',
      description: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'scheduled': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Terminé';
      case 'in_progress': return 'En cours';
      case 'scheduled': return 'Programmé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const upcomingInterventions = interventions.filter(intervention => 
    intervention.status === 'scheduled' || intervention.status === 'in_progress'
  );

  const completedInterventions = interventions.filter(intervention => 
    intervention.status === 'completed'
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Interventions - {vehicle.registration_number}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ m: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab 
              label={
                <Badge badgeContent={upcomingInterventions.length} color="primary">
                  À faire
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={completedInterventions.length} color="success">
                  Terminé
                </Badge>
              } 
            />
            <Tab label="Nouvelle intervention" />
          </Tabs>
        </Box>

        {/* Upcoming Interventions Tab */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : upcomingInterventions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune intervention programmée
              </Typography>
            </Box>
          ) : (
            <List>
              {upcomingInterventions.map((intervention, index) => (
                <React.Fragment key={intervention.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {intervention.intervention_type || 'Intervention'}
                          </Typography>
                          <Chip 
                            label={getStatusLabel(intervention.status)}
                            size="small"
                            color={getStatusColor(intervention.status) as any}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {intervention.description || 'Aucune description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {intervention.start_date && format(new Date(intervention.start_date), 'dd MMMM yyyy', { locale: fr })}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Modifier">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(intervention)}
                            sx={{ color: 'primary.main' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(intervention.id)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Completed Interventions Tab */}
        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : completedInterventions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune intervention terminée
              </Typography>
            </Box>
          ) : (
            <List>
              {completedInterventions.map((intervention, index) => (
                <React.Fragment key={intervention.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {intervention.intervention_type || 'Intervention'}
                          </Typography>
                          <Chip 
                            label="Terminé"
                            size="small"
                            color="success"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {intervention.description || 'Aucune description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Terminé le {intervention.completion_date && format(new Date(intervention.completion_date), 'dd MMMM yyyy', { locale: fr })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>

        {/* New Intervention Tab */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {editingIntervention ? 'Modifier l\'intervention' : 'Nouvelle intervention'}
              </Typography>
              
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel>Type d'intervention</InputLabel>
                  <Select
                    value={formData.intervention_type}
                    onChange={(e) => setFormData({ ...formData, intervention_type: e.target.value })}
                    label="Type d'intervention"
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          zIndex: 9999,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {interventionTypes.map(type => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez l'intervention à effectuer..."
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date de début"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    type="date"
                    label="Date de fin"
                    value={formData.completion_date || ''}
                    onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Coût (€)"
                    type="number"
                    value={formData.cost || ''}
                    onChange={(e) => setFormData({ ...formData, cost: Number(e.target.value) })}
                  />
                  <TextField
                    fullWidth
                    label="Prestataire"
                    value={formData.service_provider || ''}
                    onChange={(e) => setFormData({ ...formData, service_provider: e.target.value })}
                  />
                </Stack>

                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes additionnelles..."
                />
              </Stack>
            </CardContent>
          </Card>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {tabValue === 2 && (
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Button onClick={handleCloseForm} disabled={loading}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading || !formData.intervention_type}
            >
              {loading ? <CircularProgress size={20} /> : (editingIntervention ? 'Modifier' : 'Créer')}
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InterventionManagement;
